import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Clock, Calendar, MapPin, Users, BookOpen, LogOut, Share2, FileText, DollarSign, MessageSquare, TrendingUp, QrCode, Phone, PhoneCall, BarChart3, Tag, Star, BarChart, Bell, Zap, Mail, Car, Trophy, DoorOpen, Wrench, Bot, MessageSquareText } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, { 
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Continue with logout even if API fails
    }
    
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate("/login");
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6" data-testid="settings-header">
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-2">Settings</h1>
          <p className="font-inter text-zinc-600">Manage your preferences and availability</p>
        </div>

        <div className="space-y-4" data-testid="settings-menu">
          <button
            onClick={() => navigate('/settings/availability')}
            data-testid="nav-availability"
            className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-zinc-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-zinc-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-inter text-lg font-bold text-zinc-900">My Availability</h3>
                <p className="text-sm text-zinc-600">Set your weekly working hours</p>
              </div>
            </div>
          </button>

          {/* Resources - visible to all users */}
          <button
            onClick={() => navigate('/resources')}
            data-testid="nav-resources"
            className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-inter text-lg font-bold text-zinc-900">Resources</h3>
                <p className="text-sm text-zinc-600">Training videos, pricing, and reference materials</p>
              </div>
            </div>
          </button>

          {user?.role === "Admin" && (
            <>
              <button
                onClick={() => navigate('/settings/resources')}
                data-testid="nav-manage-resources"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Manage Resources</h3>
                    <p className="text-sm text-zinc-600">Add or edit training materials</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/zip-routing')}
                data-testid="nav-zip-routing"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Zip Code Routing</h3>
                    <p className="text-sm text-zinc-600">Map zip codes to techs</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/techs')}
                data-testid="nav-tech-management"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Tech Management</h3>
                    <p className="text-sm text-zinc-600">Add or remove screen techs</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/items-pricing')}
                data-testid="nav-items-pricing"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Items & Pricing</h3>
                    <p className="text-sm text-zinc-600">Manage products, services, and prices</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/follow-up-sequence')}
                data-testid="nav-follow-up-sequence"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Follow-up Sequence</h3>
                    <p className="text-sm text-zinc-600">Configure automated follow-up messages</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/rosie-auto-quote')}
                data-testid="nav-rosie-auto-quote"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Rosie Auto-Quote</h3>
                    <p className="text-sm text-zinc-600">Auto-send quote links when Rosie gets a call</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/review-template')}
                data-testid="nav-review-template"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Star className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Review Request Template</h3>
                    <p className="text-sm text-zinc-600">Edit the SMS sent to paid customers asking for a Google review</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/review-tracking')}
                data-testid="nav-review-tracking"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <BarChart className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Review Tracking</h3>
                    <p className="text-sm text-zinc-600">See who clicked, who left a review, and how much you owe your techs in $15 bonuses</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/ai-integration')}
                data-testid="nav-ai-integration"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-zinc-900 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">AI Integration (Grok)</h3>
                    <p className="text-sm text-zinc-600">API keys and activity log for your AI sales agent</p>
                  </div>
                </div>
              </button>

              {[["tech-assist", "Tech Assist Log", "Questions techs texted in, AI answers and office replies", MessageSquareText],
                ["tech-playbook", "Tech Playbook", "Policies Tech Assist can quote to techs", BookOpen],
                ["reply-drafts", "AI Reply Drafts", "AI-drafted customer replies waiting for approval, and what was sent", MessageSquareText]].map(([path, title, sub, Icon]) => (
                <button key={path} onClick={() => navigate(`/settings/${path}`)} data-testid={`nav-${path}`}
                  className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-[#207024] flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-inter text-lg font-bold text-zinc-900">{title}</h3>
                      <p className="text-sm text-zinc-600">{sub}</p>
                    </div>
                  </div>
                </button>
              ))}

              <button
                onClick={() => navigate('/settings/advertorials')}
                data-testid="nav-advertorials"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Articles &amp; How-To Guides</h3>
                    <p className="text-sm text-zinc-600">Advertorials for the Instant Quote funnel, plus how-to guides you text to customers</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/lead-sources')}
                data-testid="nav-lead-sources"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Lead Sources</h3>
                    <p className="text-sm text-zinc-600">Manage the source options in the New Customer dropdown (Yelp, Referral, QR Truck, etc.)</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/growth-board')}
                data-testid="nav-growth-board"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Growth Board</h3>
                    <p className="text-sm text-zinc-600">Capture ideas, score priority, track results — your business growth command center</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/notifications')}
                data-testid="nav-notifications"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Notifications</h3>
                    <p className="text-sm text-zinc-600">Free push notifications + per-event SMS toggles to control Twilio cost</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/quick-replies')}
                data-testid="nav-quick-replies"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Quick Reply Templates</h3>
                    <p className="text-sm text-zinc-600">Canned SMS responses you can one-tap insert from the Messages page</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/email-drip-reporting')}
                data-testid="nav-email-drip-reporting"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-rose-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Email Drip Reporting</h3>
                    <p className="text-sm text-zinc-600">Open / click / book conversion rates for the 5-step lead nurture sequence</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/estimate-form')}
                data-testid="nav-estimate-form"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Estimate Form</h3>
                    <p className="text-sm text-zinc-600">Configure instant estimate flow settings</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/promotions')}
                data-testid="nav-promotions"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Tag className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Specials & Promotions</h3>
                    <p className="text-sm text-zinc-600">Run discounts on instant estimates</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/funnel-analytics')}
                data-testid="nav-funnel-analytics"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Estimate Funnel</h3>
                    <p className="text-sm text-zinc-600">See where visitors drop off in the estimate flow</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/analytics')}
                data-testid="nav-analytics"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-rose-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Lead Analytics</h3>
                    <p className="text-sm text-zinc-600">Track lead sources and conversions</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/marketing-performance')}
                data-testid="nav-marketing-performance"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Marketing Performance</h3>
                    <p className="text-sm text-zinc-600">Monthly ROI, ad spend, and ROAS tracking</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/revenue-breakdown')}
                data-testid="nav-revenue-breakdown"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Revenue Breakdown</h3>
                    <p className="text-sm text-zinc-600">Revenue by service, payment methods, month-over-month</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/tech-performance')}
                data-testid="nav-tech-performance"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Tech Performance</h3>
                    <p className="text-sm text-zinc-600">Completed jobs, revenue, and reviews by screen tech</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/screen-door-funnel')}
                data-testid="nav-screen-door-funnel"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-[#207024]/10 flex items-center justify-center">
                    <DoorOpen className="w-6 h-6 text-[#207024]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Screen Door Funnel</h3>
                    <p className="text-sm text-zinc-600">V4 configurator analytics — conversion, mesh/color picks, close rate</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/drive-time')}
                data-testid="nav-drive-time"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-sky-100 flex items-center justify-center">
                    <Car className="w-6 h-6 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Drive Time</h3>
                    <p className="text-sm text-zinc-600">Per-tech miles and drive time from Home → Jobs → Home</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/open-estimates-map')}
                data-testid="nav-open-estimates-map"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Open Estimates Map</h3>
                    <p className="text-sm text-zinc-600">View nurturing estimates on a map</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/campaign-links')}
                data-testid="nav-campaign-links"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-sky-100 flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Campaign Links</h3>
                    <p className="text-sm text-zinc-600">QR codes & trackable links for marketing</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/settings/call-log')}
                data-testid="nav-call-log"
                className="w-full bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-rose-100 flex items-center justify-center">
                    <PhoneCall className="w-6 h-6 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">Call Log</h3>
                    <p className="text-sm text-zinc-600">Recent calls to your ScreenzZ number (with call-back)</p>
                  </div>
                </div>
              </button>

              </>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full bg-white rounded-xl border-2 border-red-100 shadow-sm p-5 hover:border-red-300 hover:bg-red-50 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-inter text-lg font-bold text-red-700">Logout</h3>
                <p className="text-sm text-red-600">Sign out of your account</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
